import { z } from "zod";
import { estimatePlan } from "./estimate.js";
import { AthleteSchema, CategorySchema, DivisionSchema, FormatSchema } from "./schema.js";
import type { Plan } from "./schema.js";

export const RACE_CARD_SCHEMA_VERSION = 1;

export const RaceCardSegmentSchema = z.object({
  seq: z.number().int().positive(),
  id: z.string(),
  type: z.enum(["run", "roxzone", "station", "handover"]),
  label: z.string(),
  shortLabel: z.string().max(8),
  targetSec: z.number().int().nonnegative(),
  cumulativeTargetSec: z.number().int().nonnegative(),
  athleteIds: z.array(z.string()),
});
export type RaceCardSegment = z.infer<typeof RaceCardSegmentSchema>;

/**
 * Flat, precomputed view of a plan for constrained clients (watch apps).
 * Times are whole seconds, and each segment's targetSec is the difference between
 * consecutive cumulative times, so the segments always add up to the total.
 */
export const RaceCardSchema = z.object({
  schemaVersion: z.literal(RACE_CARD_SCHEMA_VERSION),
  planId: z.string(),
  name: z.string(),
  format: FormatSchema,
  category: CategorySchema,
  division: DivisionSchema,
  athletes: z.array(AthleteSchema),
  totalTargetSec: z.number().int().nonnegative(),
  segments: z.array(RaceCardSegmentSchema),
});
export type RaceCard = z.infer<typeof RaceCardSchema>;

export function buildRaceCard(plan: Plan): RaceCard {
  const estimate = estimatePlan(plan);
  let previous = 0;
  const segments = estimate.segments.map((segment, i) => {
    const cumulativeTargetSec = Math.round(segment.endSec);
    const targetSec = cumulativeTargetSec - previous;
    previous = cumulativeTargetSec;
    return {
      seq: i + 1,
      id: segment.id,
      type: segment.type,
      label: segment.label,
      shortLabel: segment.shortLabel,
      targetSec,
      cumulativeTargetSec,
      athleteIds: segment.athleteIds,
    };
  });

  return {
    schemaVersion: RACE_CARD_SCHEMA_VERSION,
    planId: plan.id,
    name: plan.name,
    format: plan.format,
    category: plan.category,
    division: plan.division,
    athletes: plan.athletes.map(({ id, name }) => ({ id, name })),
    totalTargetSec: previous,
    segments,
  };
}
